/**
 * yt-base-picker.js — STEP 1: pick a base (B5, 2026-08-27).
 *
 * Hydrates the card grid from GET /apps/yutrients/bases (already ranked by
 * industry popularity). Selecting a card expands it full-width with the
 * contents (ingredient chips) and a "See full details" link to the base's
 * PDP. The sticky bar's NEXT goes to the builder with ?base=<slug>; the
 * "start from scratch" card (first in the grid) goes without one.
 */
(function () {
  'use strict';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(n) {
    var v = Math.round(Number(n) * 100) / 100;
    return '£' + (Number.isInteger(v) ? String(v) : v.toFixed(2));
  }

  async function boot() {
    var root = document.querySelector('[data-yt-base-picker]');
    if (!root) return;
    var grid = root.querySelector('[data-yt-base-grid]');
    var nextBtn = root.querySelector('[data-yt-base-next]');
    var stickyLabel = root.querySelector('[data-yt-base-sticky-label]');
    var builderUrl = root.getAttribute('data-builder-url') || '/pages/build-your-own';

    var bases = [];
    try {
      var res = await fetch('/apps/yutrients/bases', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (res.ok) bases = (await res.json()).bases || [];
    } catch (_e) { /* handled below */ }
    if (!bases.length) {
      grid.innerHTML = '<p>We could not load the bases just now — you can still <a href="' + esc(builderUrl) + '">build from scratch</a>.</p>';
      return;
    }

    var selected = null;

    // Category tints from the house palette (yt-tokens.css) — keyed by slug.
    var TINTS = {
      blue:   { bg: 'var(--yt-tint-blue)',   edge: 'var(--yt-accent-blue)' },
      green:  { bg: 'var(--yt-tint-green)',  edge: 'var(--yt-accent-green)' },
      orange: { bg: 'var(--yt-tint-orange)', edge: 'var(--yt-accent-orange)' },
      pink:   { bg: 'var(--yt-tint-pink)',   edge: 'var(--yt-accent-pink)' },
      yellow: { bg: 'var(--yt-tint-yellow)', edge: 'var(--yt-accent-yellow)' },
      grey:   { bg: 'var(--yt-tint-grey)',   edge: 'var(--yt-accent-grey)' }
    };
    function tintFor(slug) {
      // Founder 2026-08-27: blue for men's, pink for women's — gender first.
      if (/^mens-/.test(slug)) return TINTS.blue;
      if (/^womens-/.test(slug)) return TINTS.pink;
      if (/multi|mineral/.test(slug)) return TINTS.blue;
      if (/greens|veg|fruits|gut|joints/.test(slug)) return TINTS.green;
      if (/energy|exercise|recovery|glp1|weight/.test(slug)) return TINTS.orange;
      if (/pms|menopause|beauty/.test(slug)) return TINTS.pink;
      if (/b-complex|immunity|longevity/.test(slug)) return TINTS.yellow;
      return TINTS.grey; // sleep, anxiety, mood, cognitive + fallback
    }
    function monogram(name) {
      return name.split(/\s+/).filter(function (w) { return /^[A-Za-z0-9]/.test(w); })
        .slice(0, 2).map(function (w) { return w[0].toUpperCase(); }).join('');
    }

    // Curated shelf (founder 2026-08-27): only the featured ~10 show by
    // default; SHOW ALL reveals the rest (same cards, same behaviour).
    var featured = bases.filter(function (b) { return b.featured; });
    var others = bases.filter(function (b) { return !b.featured; });
    if (!featured.length) { featured = bases; others = []; }
    function cardHtml(b) {
      var tint = tintFor(b.slug);
      var chips = b.ingredients.map(function (ing) {
        return '<span class="yt-basechip"><b>' + esc(ing.name) + '</b> &middot; ' + esc(ing.dose_display) + '</span>';
      }).join('');
      return (
        '<article class="yt-basecard" role="button" tabindex="0" aria-pressed="false" data-yt-base-slug="' + esc(b.slug) + '">' +
          '<span class="yt-basecard__tick" aria-hidden="true"><span class="plus">+</span>' + esc(money(b.prices.sub)) + '<span style="font-size:11px;font-weight:400;">/mo</span></span>' +
          '<div class="yt-basecard__head">' +
            '<span class="yt-basecard__mono" style="background:' + tint.bg + ';">' + esc(monogram(b.name)) + '</span>' +
            '<h3 class="yt-basecard__name">' + esc(b.name) + '</h3>' +
          '</div>' +
          '<p class="yt-basecard__tag">' + esc(b.tagline || '') + '</p>' +
          '<div class="yt-basecard__foot">' +
            '<span class="yt-basecard__count" style="background:' + tint.bg + ';">' + b.n_ingredients + ' ingredients</span>' +
          '</div>' +
          '<div class="yt-basecard__detail">' +
            '<div class="yt-basecard__chips">' + chips + '</div>' +
            '<div class="yt-basecard__links">' +
              '<a class="yt-basecard__more" href="/products/base-' + esc(b.slug) + '" target="_blank" rel="noopener">See full details &rarr;</a>' +
            '</div>' +
          '</div>' +
        '</article>'
      );
    }
    // Founder 2026-09-01: "start from scratch" left the sticky and became
    // the FIRST card — same select/NEXT flow, just no base on hand-off.
    var SCRATCH = '__scratch';
    var scratchCardHtml =
      '<article class="yt-basecard yt-basecard--scratch" role="button" tabindex="0" aria-pressed="false" data-yt-base-slug="' + SCRATCH + '">' +
        '<span class="yt-basecard__tick" aria-hidden="true"><span class="plus">+</span></span>' +
        '<div class="yt-basecard__head">' +
          '<span class="yt-basecard__mono" style="background:var(--yt-tint-grey);">+</span>' +
          '<h3 class="yt-basecard__name">Fully customised</h3>' +
        '</div>' +
        '<p class="yt-basecard__tag">No base — fully customise your formula, ingredient by ingredient, at your doses.</p>' +
        '<div class="yt-basecard__foot">' +
          '<span class="yt-basecard__count" style="background:var(--yt-tint-grey);">Full ingredient range</span>' +
        '</div>' +
      '</article>';
    grid.innerHTML = scratchCardHtml + featured.map(cardHtml).join('');
    if (others.length) {
      var showAll = document.createElement('button');
      showAll.type = 'button';
      showAll.className = 'yt-basepick__showall';
      showAll.style.cssText = 'grid-column:1/-1;background:none;border:1.5px dashed var(--yt-accent-grey);border-radius:16px;padding:16px;font-family:var(--yt-font-heading);font-size:13px;letter-spacing:.1em;text-transform:uppercase;color:var(--yt-ink-tertiary);cursor:pointer;';
      showAll.textContent = 'Show all ' + bases.length + ' bases';
      showAll.addEventListener('click', function () {
        showAll.remove();
        grid.insertAdjacentHTML('beforeend', others.map(cardHtml).join(''));
        if (window.ytTrack) { try { ytTrack('base_showall_clicked', {}); } catch (_e) {} }
      });
      grid.appendChild(showAll);
    }

    var panel = root.querySelector('[data-yt-base-panel]');
    function renderPanel(base) {
      if (!panel) return;
      if (base === SCRATCH) {
        panel.innerHTML =
          '<div class="yt-basepanel__head">' +
            '<span class="yt-basecard__mono" style="background:var(--yt-tint-grey);">+</span>' +
            '<h3 class="yt-basepanel__name">Fully customised</h3>' +
          '</div>' +
          '<p class="yt-basepanel__desc">No base \u2014 you\u2019ll pick every ingredient and dose yourself in the next step, from the full range.</p>';
        return;
      }
      if (!base) {
        panel.innerHTML = '<p class="yt-basepanel__empty">Select a base to see what\u2019s inside.</p>';
        return;
      }
      var tint = tintFor(base.slug);
      var chips = base.ingredients.map(function (ing) {
        return '<span class="yt-basechip"><b>' + esc(ing.name) + '</b> &middot; ' + esc(ing.dose_display) + '</span>';
      }).join('');
      panel.innerHTML =
        '<div class="yt-basepanel__head">' +
          '<span class="yt-basecard__mono" style="background:' + tint.bg + ';">' + esc(monogram(base.name)) + '</span>' +
          '<h3 class="yt-basepanel__name">' + esc(base.name) + '</h3>' +
        '</div>' +
        '<p class="yt-basepanel__price">' + esc(money(base.prices.sub)) + '<span style="font-size:14px;">/mo</span><small>on the 30-day plan</small></p>' +
        '<p class="yt-basepanel__ladder">' + esc(money(base.prices.monthly_90d)) + '/mo on 90-day &middot; ' + esc(money(base.prices.one_time)) + ' one-time</p>' +
        '<p class="yt-basepanel__desc">' + esc(base.description || base.tagline || '') + '</p>' +
        '<div class="yt-basecard__chips">' + chips + '</div>' +
        '<div class="yt-basecard__links"><a class="yt-basecard__more" href="/products/base-' + esc(base.slug) + '" target="_blank" rel="noopener">See full details &rarr;</a></div>';
    }
    renderPanel(null);
    if (panel) panel.hidden = false;
    if (window.ytTrack) { try { ytTrack('funnel_step_viewed', { step: 1, page: 'pick-a-base' }); } catch (_e) {} }

    function select(slug) {
      // Clicking the selected card again UNSELECTS it (founder 2026-08-27).
      selected = (selected === slug) ? null : slug;
      grid.querySelectorAll('.yt-basecard').forEach(function (card) {
        var cardSlug = card.getAttribute('data-yt-base-slug');
        var match = selected != null && cardSlug === selected;
        card.classList.toggle('is-selected', match);
        card.setAttribute('aria-pressed', String(match));
        var tick = card.querySelector('.yt-basecard__tick');
        if (tick) {
          if (cardSlug === SCRATCH) {
            tick.innerHTML = match ? '&times;' : '<span class="plus">+</span>';
          } else {
            var cb = bases.find(function (x) { return x.slug === cardSlug; });
            tick.innerHTML = match
              ? '&times;'
              : '<span class="plus">+</span>' + esc(money(cb ? cb.prices.sub : 0)) + '<span style="font-size:11px;font-weight:400;">/mo</span>';
          }
        }
      });
      nextBtn.disabled = !selected;
      var base = selected && selected !== SCRATCH
        ? bases.find(function (b) { return b.slug === selected; })
        : null;
      renderPanel(selected === SCRATCH ? SCRATCH : base);
      // Founder 2026-09-01: price only on the left — no base name / plan
      // line — so the sticky matches the rest of the funnel. Scratch has
      // no base price, so its label stays empty.
      if (stickyLabel) {
        stickyLabel.innerHTML = base
          ? esc(money(base.prices.sub)) + '<small>/mo &middot; ' + esc(money(Math.round((Number(base.prices.sub) / 30) * 100) / 100)) + ' a day</small>'
          : '';
      }
      if (base && window.ytTrack) { try { ytTrack('base_selected', { base: selected }); } catch (_e) {} }
    }

    grid.addEventListener('click', function (ev) {
      if (ev.target.closest('a')) return; // "See full details" navigates
      var card = ev.target.closest('.yt-basecard');
      if (!card) return;
      select(card.getAttribute('data-yt-base-slug'));
    });
    grid.addEventListener('keydown', function (ev) {
      if (ev.key !== 'Enter' && ev.key !== ' ') return;
      var card = ev.target.closest('.yt-basecard');
      if (!card) return;
      ev.preventDefault();
      select(card.getAttribute('data-yt-base-slug'));
    });

    // ?fresh=1 on the picker (cart drawer's "+ Build another blend") rides
    // through to the builder so the previous build's saved add-ons clear.
    var fresh = new URLSearchParams(window.location.search).get('fresh') === '1';
    nextBtn.addEventListener('click', function () {
      if (!selected) return;
      // Founder 2026-08-28 (rev 2): the funnel keeps its shape — step 1
      // hands off to the BUILDER (step 2 customise, step 3 name). The
      // NEW landing-style page is the step-4 PDP the builder redirects
      // to with ?blend=; only ad/direct traffic lands on it straight.
      if (selected === SCRATCH) {
        if (window.ytTrack) { try { ytTrack('scratch_clicked', {}); } catch (_e) {} }
        window.location.assign(builderUrl + (fresh ? '?fresh=1' : ''));
        return;
      }
      if (window.ytTrack) { try { ytTrack('base_next_clicked', { base: selected }); } catch (_e) {} }
      window.location.assign(builderUrl + '?base=' + encodeURIComponent(selected) + (fresh ? '&fresh=1' : ''));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
